import { supabaseClient } from '../supabase';
import type { TicketmasterTranslation } from './types';
import type { SupportedLanguages } from '../../i18n';
import { logger } from '../logger';
import { SUPABASE_TABLES } from '../supabase/tables';

class TicketmasterTranslations {
  private static instance: TicketmasterTranslations;

  private constructor() {}

  public static getInstance(): TicketmasterTranslations {
    if (!TicketmasterTranslations.instance) {
      TicketmasterTranslations.instance = new TicketmasterTranslations();
    }
    return TicketmasterTranslations.instance;
  }

  private async generateHash(text: string): Promise<string> {
    const msgUint8 = new TextEncoder().encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  public async translateDescription(
    description: string,
    targetLang: SupportedLanguages,
    translationTableId: string,
    itemId: string
  ): Promise<string> {
    if (!description || targetLang === 'fr') return description;

    try {
      logger.debug('Starting Ticketmaster translation', {
        category: 'Translation',
        data: { 
          translationTableId,
          itemId,
          targetLang,
          descriptionLength: description.length
        }
      });

      // Générer le hash du texte source
      const sourceHash = await this.generateHash(description);

      // Chercher une traduction existante
      const { data: records, error } = await supabaseClient
        .from(SUPABASE_TABLES.SECTION_TRANSLATIONS.name)
        .select('*')
        .eq('source_hash', sourceHash)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      // Si on trouve un record existant
      if (records) {
        const translationField = targetLang;
        const existingTranslation = records[translationField];

        // Si la traduction existe déjà dans ce record
        if (existingTranslation && existingTranslation.trim() !== '' && existingTranslation !== '\n') {
          logger.debug('Found existing translation', {
            category: 'Translation',
            data: { 
              recordId: records.id,
              sourceHash,
              translationPreview: existingTranslation.substring(0, 100) + '...'
            }
          });
          return existingTranslation;
        }

        // Si le record existe mais pas la traduction dans cette langue
        const translatedText = await this.translateWithGoogleAPI(description, targetLang);

        logger.debug('Updating existing record with new translation', {
          category: 'Translation',
          data: { 
            recordId: records.id,
            sourceHash,
            targetLang,
            translationPreview: translatedText.substring(0, 100) + '...'
          }
        });

        await supabaseClient
          .from(SUPABASE_TABLES.SECTION_TRANSLATIONS.name)
          .update({
            [targetLang]: translatedText,
            last_used: new Date().toISOString()
          })
          .eq('id', records.id);

        return translatedText;
      }

      // Si aucun record n'existe, traduire et créer un nouveau
      const translatedText = await this.translateWithGoogleAPI(description, targetLang);

      logger.debug('Creating new translation record', {
        category: 'Translation',
        data: { 
          sourceHash,
          itemId,
          translationPreview: translatedText.substring(0, 100) + '...'
        }
      });

      await supabaseClient
        .from(SUPABASE_TABLES.SECTION_TRANSLATIONS.name)
        .insert({
          source_hash: sourceHash,
          item_id: itemId,
          fr: description,
          [targetLang]: translatedText,
          last_used: new Date().toISOString(),
          created_at: new Date().toISOString()
        });

      return translatedText;

    } catch (error) {
      logger.error('Translation error', {
        category: 'Translation',
        data: { 
          error,
          itemId,
          targetLang,
          descriptionLength: description.length
        }
      });
      return description;
    }
  }

  private async translateWithGoogleAPI(
    text: string,
    targetLang: SupportedLanguages
  ): Promise<string> {
    const apiKey = import.meta.env.VITE_GOOGLE_TRANSLATE_API_KEY;
    if (!apiKey) {
      throw new Error('Missing Google Translate API key');
    }

    logger.debug('Translating with Google API', {
      category: 'Translation',
      data: { 
        textLength: text.length,
        textPreview: text.substring(0, 200) + '...',
        targetLang
      }
    });

    const response = await fetch(
      `https://translation.googleapis.com/language/translate/v2?key=${apiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          q: text,
          source: 'fr',
          target: targetLang,
          format: 'text'
        })
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('Google Translate API error', {
        category: 'Translation',
        data: {
          status: response.status,
          statusText: response.statusText,
          errorText
        }
      });
      throw new Error(`Translation API error: ${response.status}`);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(data.error.message);
    }

    const translatedText = data.data.translations[0].translatedText;

    logger.debug('Google translation successful', {
      category: 'Translation',
      data: { 
        targetLang,
        translationPreview: translatedText.substring(0, 200) + '...',
        originalLength: text.length,
        translatedLength: translatedText.length
      }
    });

    return translatedText;
  }

  /**
   * Translate a category or type term using the Edge Function
   * @param term The term to translate
   * @param sourceLanguage The source language of the term
   * @returns Object containing translations for all supported languages
   */
  public async translateCategoryOrType(
    term: string,
    sourceLanguage: SupportedLanguages
  ): Promise<Record<string, string>> {
    try {
      // Vérifier d'abord si le terme existe déjà dans trad_cat_type
      const { data: existingTranslations, error: checkError } = await supabaseClient
        .from('trad_cat_type')
        .select('*')
        .eq(sourceLanguage, term)
        .single();

      // Log détaillé de la recherche dans trad_cat_type
      logger.debug('Recherche du terme dans trad_cat_type', {
        category: 'Translation',
        data: { 
          terme: term,
          locale: sourceLanguage,
          colonne_recherche: sourceLanguage,
          traductions_existantes: existingTranslations ? 
            Object.fromEntries(
              Object.entries(existingTranslations)
                .filter(([key]) => ['fr', 'en', 'es', 'it', 'pt', 'de', 'nl', 'ja', 'zh', 'ru'].includes(key))
            ) : null,
          erreur: checkError
        }
      });

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw checkError;
      }

      // Si le terme existe déjà, retourner les traductions existantes
      if (existingTranslations) {
        logger.debug('Terme trouvé dans trad_cat_type', {
          category: 'Translation',
          data: { 
            terme: term,
            id: existingTranslations.id,
            traductions: Object.fromEntries(
              Object.entries(existingTranslations)
                .filter(([key]) => ['fr', 'en', 'es', 'it', 'pt', 'de', 'nl', 'ja', 'zh', 'ru'].includes(key))
            )
          }
        });

        // Extraire les traductions pour toutes les langues supportées
        const translations: Record<string, string> = {};
        ['fr', 'en', 'es', 'it', 'pt', 'de', 'nl', 'ja', 'zh', 'ru'].forEach(lang => {
          translations[lang] = existingTranslations[lang] || term;
        });

        return translations;
      }

      // Si le terme n'existe pas, appeler l'Edge Function pour le traduire
      logger.debug('Terme non trouvé dans trad_cat_type, appel à l\'Edge Function', {
        category: 'Translation',
        data: { terme: term, sourceLanguage }
      });

      // Get Supabase URL from environment
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Missing Supabase URL');
      }

      // Get Supabase anon key from environment
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      if (!supabaseAnonKey) {
        throw new Error('Missing Supabase anon key');
      }

      // Call the Edge Function
      const response = await fetch(
        `${supabaseUrl}/functions/v1/translate-cat-type`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`
          },
          body: JSON.stringify({
            term,
            sourceLanguage
          })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Erreur de l\'Edge Function', {
          category: 'Translation',
          data: { 
            status: response.status, 
            errorText,
            terme: term
          }
        });
        throw new Error(`Edge Function error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        logger.error('Échec de traduction dans l\'Edge Function', {
          category: 'Translation',
          data: { 
            error: data.error,
            terme: term
          }
        });
        throw new Error(data.error || 'Unknown error from Edge Function');
      }

      logger.debug('Traduction réussie via Edge Function', {
        category: 'Translation',
        data: { 
          terme: term, 
          sourceLanguage,
          traductions: data.translations
        }
      });

      return data.translations || {};

    } catch (error) {
      logger.error('Erreur lors de la traduction du terme', {
        category: 'Translation',
        data: { error, term, sourceLanguage }
      });
      
      // Return a fallback with just the original term
      const fallback: Record<string, string> = {};
      fallback[sourceLanguage] = term;
      return fallback;
    }
  }
}

export const ticketmasterTranslations = TicketmasterTranslations.getInstance();